import React, { useState } from 'react';
import { Column, Row } from '@src/components/Utils';
import { useQueryCatalogTaxonomy } from '@src/queries/products/useQueryCatalogTaxonomy';
import { useQueryProductGroups, useQueryAllProductGroups } from '@src/queries/products/useQueryProductGroups';
import { ProductGroupLinkable } from '@src/components/Product/ProductGroupLinkable';
import { Loader } from '@src/components/Loader';
import { Modal } from '@src/components/Modal';
import { InfiniteScroller } from '@src/components/InfiniteScroller';
import { PerfectScrollbar } from '@src/components/PerfectScrollbar';
import CircularProgress from '@mui/material/CircularProgress';
import { classes, style } from 'typestyle';
import { ADALONG_COLORS, ThemeStyle } from '@src/styles';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import { TaxonomySection } from '@src/components/Product/Taxonomy/TaxonomySection';
import { SearchAndSelectBar } from '@src/components/SearchAndSelectBar';
import { Hide } from '@src/components/Hide';
import { FormError } from '@src/components/Form/FormError';
import { BarelyVisibleText } from '@src/components/BarelyVisibleText';
import { FolderButton } from '@src/components/FolderButton';
import { ImageList, ImageListItem, Typography } from '@mui/material';
import { LinkedFilter, LinkedFilterValue } from '@src/components/Product/LinkedFilter';
import { ApiTypes, MediaView, ProductGroupView } from 'common';
import { useProductLinking } from '@src/pages/Media/DetailedMediaView/Tabs/hooks/useProductLinking';
import { ArrayHelper } from '@src/services/helpers/array';
import { SectionTitle } from '@src/components/SectionTitle';
import { useQueryUserConfig } from '@src/queries/users/useQueryUserConfig';
import { useObjectLabels } from '@src/pages/Media/DetailedMediaView/Tabs/hooks/useObjectLabels';
import { useQuerySuggestions } from '@src/queries/products/useQuerySuggestions';
import { CATEGORY_SEPARATOR } from '@src/config/product';
import { useMutationMediaDescribe } from '@src/queries/media/useMutationMediaDescribe';
import { useAtomValue } from 'jotai';
import { userAtom } from '@src/state/userAtom';
import { Select } from '@src/components/Select';

const MY_COUNTRIES = 'MY_COUNTRIES';
type SortOrder = ApiTypes.SortOrder;

interface Props {
  media: MediaView;
  linkedFilter: LinkedFilterValue;
  selectedCategoryPath: string[];
  productSearchFilter: string;
  setLinkedFilter: (linkedFilter: LinkedFilterValue) => void;
  setSelectedCategoryPath: (selected: string[]) => void;
  setProductSearchFilter: (value: string) => void;
}

export const ProductTab = ({
  media,
  linkedFilter,
  selectedCategoryPath,
  productSearchFilter,
  setLinkedFilter,
  setSelectedCategoryPath,
  setProductSearchFilter,
}: Props) => {
  // Hooks
  const { data: userConfig } = useQueryUserConfig();
  const cachedUser = useAtomValue(userAtom);
  const user = userConfig?.user ?? cachedUser ?? null;
  const catalogCountries = user?.catalogCountries ?? [];
  const hasCatalogCountries = catalogCountries.length > 0;
  const [selectedCatalogCountry, setSelectedCatalogCountry] = useState<string>(MY_COUNTRIES);
  const showProductSuggestions = user?.groupFeatureDisabler?.product_suggestions === false;
  const objectLabels = useObjectLabels(media);
  const { linkProducts, unlinkProducts, isLoading: isLoadingProductLinking } = useProductLinking(media);
  const {
    data: suggestionsResponse,
    isLoading: isLoadingSuggestions,
    refetch: refetchSuggestions,
  } = useQuerySuggestions(media.source.full_id);
  const {
    mutateAsync: describeMedia,
    isPending: isGeneratingSuggestions,
  } = useMutationMediaDescribe();
  const [suggestionsGenerationError, setSuggestionsGenerationError] = useState<string | null>(null);

  // Queries
  const { data: taxonomy, isLoading: isLoadingTaxonomy, error: errorTaxonomy } = useQueryCatalogTaxonomy();
  const filterByLinkedProducts = decideProductLinkedFilter({ objectLabels, linkedFilter });
  const productTypes = ArrayHelper.filterNulls([selectedCategoryPath?.join('/')]);
  const productIds = filterByLinkedProducts || undefined;
  const {
    data: resProductGroups,
    isLoading: isLoadingProductGroups,
    error: errorProductGroups,
    fetchNextPage: fetchMoreProductGroups,
    hasNextPage: canFetchMoreProductGroups,
  } = useQueryProductGroups(
    {
      text: productSearchFilter,
      productTypes,
      productIds,
      sortOrder: getProductSortOrderForSearch(productSearchFilter),
      catalogCountries: getCatalogCountryFilter(selectedCatalogCountry, catalogCountries),
      applyLocalization: true,
    },
    {
      // Wait for the user config before fetching so the first request cannot be
      // made without the user's country filter. Keep the linked-products guard.
      enabled: user !== null && filterByLinkedProducts !== false,
    }
  );
  const { data: resAllProductGroups, isLoading: isLoadingAllProductGroups, error: errorAllProductGroups } = useQueryAllProductGroups();

  const groupName: string = user?.group?.name ?? '';
  const productGroups: ProductGroupView[] = resProductGroups?.pages.flatMap((p) => p.productGroups) ?? [];
  const allGroups = resAllProductGroups ?? [];
  const productListAlone = errorTaxonomy !== null;
  const captionSuggestions = searchSuggestions(media.caption ?? '', groupName);
  const suggestions = suggestionsResponse?.suggestions ?? [];
  const hasSuggestionsEmbeddings = suggestionsResponse?.hasEmbeddings ?? false;
  const suggestedProductGroups = filterSuggestedProductGroups({
    suggestedProductGroups: suggestions,
    selectedCategoryPath,
  });
  const hasSuggestedProductGroups = showProductSuggestions && suggestedProductGroups.length > 0;
  const canGenerateSuggestionsOnDemand =
    showProductSuggestions
    && !hasSuggestedProductGroups
    && !isLoadingSuggestions
    && !hasSuggestionsEmbeddings
    && !!media.source.full_id;
  const suggestedProductCount = suggestedProductGroups.reduce((count, group) => count + group.products.length, 0);
  const [suggestedSectionCollapsed, setSuggestedSectionCollapsed] = useState(false);

  const handleGenerateSuggestions = async () => {
    setSuggestionsGenerationError(null);

    const response = await describeMedia({
      fullIds: [media.source.full_id],
    });
    const result = response.results[0];

    if (!result) {
      setSuggestionsGenerationError(lang.generationUnknownError);
      return;
    }

    if (result.status === 'generated' || result.status === 'already_exists') {
      await refetchSuggestions();
      return;
    }

    setSuggestionsGenerationError(result.error ?? lang.generationFailed);
  };

  const handleProductSearch = (value: string) => {
    setProductSearchFilter(value);
  };

  return (
    <Row className={productTabClass}>
      <Hide hide={productListAlone}>
        <Column className={classes(taxonomyTabColumn)}>
          <SectionTitle size='small'>{lang.taxonomy.toUpperCase()}</SectionTitle>
          <TaxonomySection
            taxonomy={taxonomy}
            selectedCategoryPath={selectedCategoryPath}
            setSelectedCategoryPath={setSelectedCategoryPath}
            productGroups={allGroups}
          />
        </Column>
      </Hide>
      <div className={classes(productTabListColumn)} data-alone={productListAlone}>
        <Column className={searchClass}>
          <Row className={searchControlsRowClass}>
            <SearchAndSelectBar
              className={productSearchBarClass}
              inputValue={productSearchFilter}
              appearance='dark'
              layout='singleLine'
              handlers={{
                onText: handleProductSearch,
                onCloseBar: () => handleProductSearch(''),
              }}
            />
            <LinkedFilter value={linkedFilter} onChange={setLinkedFilter} />
          </Row>
          <InfiniteScroller
            noMore={!canFetchMoreProductGroups}
            load={() => fetchMoreProductGroups().then()}
            container={(props, children) => (
              <PerfectScrollbar className={resultsScrollerClass} {...props}>
                <div className={resultsContentClass}>{children}</div>
              </PerfectScrollbar>
            )}
          >
            <>
              <Hide hide={captionSuggestions.length === 0}>
                <div className={captionSuggestionsSectionClass}>
                  <Typography variant='caption' className={captionSuggestionsTitleClass}>
                    {lang.searchSuggestions}
                  </Typography>
                  <ImageList cols={3} className={captionSuggestionsListClass}>
                    {captionSuggestions.map((suggestion, i) => (
                      <ImageListItem
                        rows={1}
                        cols={3}
                        className={suggestionClass}
                        key={i}
                        onClick={() => handleProductSearch(suggestion)}
                      >
                        <Typography variant='subtitle2' className={suggestionChipClass}>
                          {suggestion}
                        </Typography>
                      </ImageListItem>
                    ))}
                  </ImageList>
                </div>
              </Hide>

              <Hide hide={!hasSuggestedProductGroups}>
                <div className={suggestedSectionClass}>
                  <Row
                    className={classes(sectionHeaderRowClass, suggestedSectionHeaderButtonClass)}
                    onClick={() => setSuggestedSectionCollapsed((collapsed) => !collapsed)}
                    role='button'
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSuggestedSectionCollapsed((collapsed) => !collapsed);
                      }
                    }}
                    aria-label={suggestedSectionCollapsed ? lang.expandSuggestedProducts : lang.collapseSuggestedProducts}
                  >
                    <div>
                      <SectionTitle size='small'>{lang.suggestedProducts}</SectionTitle>
                      <Typography variant='caption' className={sectionHelperTextClass}>
                        {lang.suggestedProductsDescription}
                      </Typography>
                    </div>
                    <Row className={suggestedSectionControlsClass}>
                      <div className={sectionCountBadgeClass}>{suggestedProductCount}</div>
                      <div
                        className={suggestedSectionToggleClass}
                        aria-hidden='true'
                      >
                        <FolderButton opened={suggestedSectionCollapsed} />
                      </div>
                    </Row>
                  </Row>
                  <Hide hide={suggestedSectionCollapsed}>
                    <div className={suggestedProductListClass}>
                      {suggestedProductGroups.map((suggestion) => (
                        <div key={suggestion.name} className={suggestedCardWrapperClass}>
                          <div className={suggestedCardBadgeClass}>{lang.suggestedBadge}</div>
                          <ProductGroupLinkable
                            productGroup={suggestion}
                            productIdsExact={objectLabels.productIdsExact}
                            productIdsSimilar={objectLabels.productIdsSimilar}
                            linkProducts={linkProducts}
                            unlinkProducts={unlinkProducts}
                            hideLinkButtonsForMainRow={suggestion.products.length > 1}
                            disableLinking={false}
                          />
                        </div>
                      ))}
                    </div>
                  </Hide>
                </div>
              </Hide>

              <Hide hide={!canGenerateSuggestionsOnDemand}>
                <div className={suggestedSectionClass}>
                  <Row className={sectionHeaderRowClass}>
                    <div>
                      <SectionTitle size='small'>{lang.suggestedProducts}</SectionTitle>
                      <Typography variant='caption' className={sectionHelperTextClass}>
                        {lang.generateSuggestionsDescription}
                      </Typography>
                    </div>
                  </Row>
                  <div className={generateSuggestionsActionRowClass}>
                    <button
                      type='button'
                      className={generateSuggestionsButtonClass}
                      onClick={() => handleGenerateSuggestions().catch((error) => {
                        setSuggestionsGenerationError(error?.message ?? lang.generationFailed);
                      })}
                      disabled={isGeneratingSuggestions}
                    >
                      {isGeneratingSuggestions ? (
                        <CircularProgress size={16} thickness={5} className={generateSuggestionsSpinnerClass} />
                      ) : null}
                      <span>
                        {isGeneratingSuggestions ? lang.generatingSuggestions : lang.generateSuggestions}
                      </span>
                    </button>
                  </div>
                  <Hide hide={!suggestionsGenerationError}>
                    <div className={generateSuggestionsErrorClass}>
                      <FormError error={suggestionsGenerationError ?? undefined} />
                    </div>
                  </Hide>
                </div>
              </Hide>

              <div className={catalogSectionClass}>
                <div className={catalogSectionHeaderClass}>
                  <div className={catalogSectionDividerClass} />
                  <SectionTitle size='small'>{lang.allProducts}</SectionTitle>
                </div>
                <Typography variant='caption' className={sectionHelperTextClass}>
                  {lang.allProductsDescription}
                </Typography>
                <Row className={countryFilterRowClass}>
                  <Hide hide={!hasCatalogCountries}>
                    <div className={filterControlClass}>
                      <span className={countryFilterLabelClass}>
                        <SectionTitle size='small'>{lang.filterByCountry.toUpperCase()}</SectionTitle>
                      </span>
                      <Select
                        className={countryFilterSelectClass}
                        options={[
                          { value: MY_COUNTRIES, text: 'My countries' },
                          ...catalogCountries.map((country) => ({ value: country, text: country })),
                        ]}
                        value={selectedCatalogCountry}
                        onChange={setSelectedCatalogCountry}
                        variant='adalong'
                      />
                    </div>
                  </Hide>
                </Row>
                <div className={infoMessageClass}>
                  <FormError error={errorProductGroups?.message} />
                  <Hide hide={!!productGroups?.length || isLoadingProductGroups || isLoadingAllProductGroups}>
                    <BarelyVisibleText size='medium'>{lang.noProducts}</BarelyVisibleText>
                    <Hide hide={linkedFilter === LinkedFilterValue.all}>
                      <BarelyVisibleText size='medium'>{lang.noLinkedProductTips}</BarelyVisibleText>
                    </Hide>
                  </Hide>
                  <Hide hide={!isLoadingProductGroups}>
                    <Loader />
                  </Hide>
                </div>
                <div className={productGroupListClass}>
                  {productGroups?.map((productGroup) => (
                    <ProductGroupLinkable
                      key={productGroup.name}
                      productGroup={productGroup}
                      productIdsExact={objectLabels.productIdsExact}
                      productIdsSimilar={objectLabels.productIdsSimilar}
                      linkProducts={linkProducts}
                      unlinkProducts={unlinkProducts}
                      disableLinking={false}
                    />
                  ))}
                </div>
              </div>
            </>
          </InfiniteScroller>
        </Column>
      </div>
      {(isLoadingProductLinking || isLoadingTaxonomy) ? (
        <Modal style={{ zIndex: 10 }}>
          <CircularProgress style={{ margin: 'auto' }} />
        </Modal>
      ) : null}
    </Row>
  );
};

/**
 * Decide if it should filter by linked products depending on what labels are selected
 */
export const decideProductLinkedFilter = (params: {
  objectLabels: { productIdsExact: string[]; productIdsSimilar: string[] },
  linkedFilter: LinkedFilterValue
}) => {
  const { objectLabels, linkedFilter } = params;
  const allExactLabelProductIds = objectLabels.productIdsExact ?? [];
  const allSimilarLabelProductIds = objectLabels.productIdsSimilar ?? [];

  switch (linkedFilter) {
    case LinkedFilterValue.exact:
      if (allExactLabelProductIds.length === 0) {
        return false;
      }
      return allExactLabelProductIds;

    case LinkedFilterValue.similar:
      if (allSimilarLabelProductIds.length === 0) {
        return false;
      }
      return allSimilarLabelProductIds;

    case LinkedFilterValue.all:
      return [];
  }
}

/**
 * Build the catalog country query from the selector value. An empty catalog
 * means there is no country restriction, while MY_COUNTRIES expands to all of
 * the countries configured for the current user.
 */
export const getCatalogCountryFilter = (
  selectedCatalogCountry: string,
  catalogCountries: string[],
): string[] | undefined => {
  if (catalogCountries.length === 0) {
    return undefined;
  }

  return selectedCatalogCountry === MY_COUNTRIES
    ? catalogCountries
    : [selectedCatalogCountry];
};

/**
 * Sort the catalog by media count when no search is active and by relevance
 * whenever the search contains non-whitespace text.
 */
export const getProductSortOrderForSearch = (searchText: string): SortOrder => {
  return searchText.trim() === '' ? 'mediaCount-desc' : 'relevance-desc';
};

/**
 * Function to suggest product searches.
 * @param caption
 * @param searchName Use to get sentences in which searchName is present
 *
 * The caption is split using '|', '\n', '.', ',' and ';' characters, then
 * filtered to consider only phrases smaller than 200 characters that
 * contains the groupName. Then emojis and empty-spaces are removed.
 */
export const searchSuggestions = (caption: string, searchName: string): string[] => {
  const searchValue = RegExp(`\\S*${searchName}\\S*`, 'ig');
  return (
    (caption || '')
      .split(/[\n|,\\.;]/)
      .filter((l) => l.length < 200 && RegExp(`(?<!#)${searchName}`, 'gi').exec(l))
      .map((l) =>
        l
          .replaceAll(searchValue, '')
          // Remove emoticon unicodes
          .replaceAll(RegExp('[^\u0020-\u007e\u00a0-\u00ff\u0152\u0153\u0178]', 'g'), '')
      )
      // Remove empty lines
      .filter((l) => l.length > 1)
  );
}

export const filterSuggestedProductGroups = (params: {
  suggestedProductGroups: ProductGroupView[];
  selectedCategoryPath: string[];
}): ProductGroupView[] => {
  const { suggestedProductGroups, selectedCategoryPath } = params;
  if (selectedCategoryPath.length === 0) {
    return suggestedProductGroups;
  }

  return suggestedProductGroups
    .map((group) => ({
      ...group,
      products: group.products.filter((product) => matchesCategoryPath(product, selectedCategoryPath)),
    }))
    .filter((group) => group.products.length > 0);
};

const matchesCategoryPath = (product: ProductViewWithType, selectedCategoryPath: string[]): boolean => {
  const typeList = product.product_type?.split(CATEGORY_SEPARATOR) ?? [];
  const pathMatches = typeList.find((type) => selectedCategoryPath.includes(type))
    ? (typeList.length > selectedCategoryPath.length
      || typeList[typeList.length - 1] === selectedCategoryPath[selectedCategoryPath.length - 1])
    : null;

  return !!pathMatches;
}

type ProductViewWithType = ProductGroupView['products'][number] & {
  product_type?: string;
};

const lang = LocalizedStrings({
  en: {
    filterByCountry: 'Filter products by country',
    taxonomy: 'Categories',
    description: 'Search for a product',
    searchSuggestions: 'Caption suggestions',
    suggestedProducts: 'Suggested for this media',
    suggestedProductsDescription: 'Recommended from detected content and caption context. Suggestions may include products outside the selected country filter.',
    suggestedBadge: 'Suggested',
    collapseSuggestedProducts: 'Collapse suggested products',
    expandSuggestedProducts: 'Expand suggested products',
    generateSuggestions: 'Generate suggestions',
    generatingSuggestions: 'Generating suggestions...',
    generateSuggestionsDescription: 'Suggestions are not available yet for this media. Generate them on demand.',
    generationFailed: 'Could not generate suggestions for this media.',
    generationUnknownError: 'Unexpected generation response.',
    allProducts: 'All products',
    allProductsDescription: 'Browse the full catalog and refine results with search or categories',
    noBoxes: 'No objects detected in this image',
    noProducts: 'No products found',
    noLinkedProductTips: 'Click on "All products" to see the catalog',
  },
});

const productTabClass = style({
  overflow: 'hidden',
  height: '100%',
  position: 'relative',
});

const taxonomyTabColumn = style({
  borderRight: ThemeStyle.borderSeparator,
  width: '40%',
  height: '100%',
  boxSizing: 'border-box',
  flexGrow: 'initial',
  padding: '10px 14px 0',
  position: 'relative',
});

const countryFilterRowClass = style({
  alignItems: 'center',
  flexWrap: 'wrap',
  flexGrow: 0,
  gap: '8px 12px',
  marginTop: 12,
  marginBottom: 4,
  minWidth: 0,
});

const filterControlClass = style({
  display: 'flex',
  alignItems: 'center',
  flex: '1 1 260px',
  gap: 12,
  minWidth: 0,
});

const countryFilterLabelClass = style({
  flex: '1 1 auto',
  minWidth: 0,
  overflowWrap: 'anywhere',
});

const countryFilterSelectClass = style({
  boxSizing: 'border-box',
  flex: '0 1 180px',
  width: 180,
  maxWidth: '100%',
  minWidth: 0,
  $nest: {
    '& .MuiSelect-select': {
      minWidth: 0,
    },
  },
});

const searchControlsRowClass = style({
  alignItems: 'center',
  flexWrap: 'wrap',
  flexGrow: 0,
  gap: 8,
  minWidth: 0,
});

const productTabListColumn = style({
  width: '60%',
  flexGrow: 'initial',
  position: 'relative',
  padding: '21px 22px 0',
  $nest: {
    '&[data-alone="true"]': {
      width: '100%',
    },
  },
});

const searchClass = style({
  height: '100%',
});

const resultsScrollerClass = style({
  textAlign: 'left',
  position: 'relative',
  overflow: 'hidden',
  flex: 1,
  marginRight: 5,
  marginTop: 16,
});

const resultsContentClass = style({
  paddingBottom: 24,
});

const productSearchBarClass = style({
  flex: '1 1 220px',
  minWidth: 0,
  minHeight: 40,
  boxSizing: 'border-box',
});

const captionSuggestionsSectionClass = style({
  marginBottom: 24,
});

const captionSuggestionsTitleClass = style({
  display: 'block',
  marginBottom: 8,
  color: ADALONG_COLORS.LIGHT_GRAY,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
});

const captionSuggestionsListClass = style({
  marginTop: 0,
});

const infoMessageClass = style({
  marginTop: 20,
  padding: 15,
  textAlign: 'center',
  $nest: {
    '&:empty': {
      display: 'none',
    },
  },
});

const suggestionClass = style({
  fontSize: '0.7em',
  $nest: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
});

const suggestionChipClass = style({
  padding: '5px 10px',
  border: `1px solid ${ADALONG_COLORS.DARK_GRAY}`,
  borderRadius: 12,
  width: 'fit-content',
  color: ADALONG_COLORS.SMOOTH_WHITE,
  backgroundColor: ADALONG_COLORS.SMOOTH_BLACK,
});

const suggestedSectionClass = style({
  marginBottom: 28,
  padding: '16px 16px 8px',
  borderRadius: 14,
  backgroundColor: ADALONG_COLORS.SMOOTH_BLACK,
  border: `1px solid ${ADALONG_COLORS.DARK_GRAY}`,
  boxShadow: `inset 3px 0 0 ${ADALONG_COLORS.LIGHT_YELLOW}`,
});

const suggestedProductListClass = style({
  marginTop: 10,
});

const suggestedCardWrapperClass = style({
  position: 'relative',
});

const suggestedCardBadgeClass = style({
  position: 'absolute',
  top: 10,
  left: 10,
  zIndex: 1,
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: ADALONG_COLORS.DARK_BLACK,
  backgroundColor: ADALONG_COLORS.LIGHT_YELLOW,
});

const generateSuggestionsActionRowClass = style({
  marginTop: 16,
});

const generateSuggestionsButtonClass = style({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  minHeight: 42,
  padding: '10px 16px',
  borderRadius: 999,
  border: `1px solid ${ADALONG_COLORS.LIGHT_YELLOW}`,
  backgroundColor: ADALONG_COLORS.DARKER_GRAY,
  color: ADALONG_COLORS.LIGHT_WHITE,
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
  transition: 'transform 120ms ease, background-color 120ms ease, opacity 120ms ease',
  $nest: {
    '&:hover:not(:disabled)': {
      backgroundColor: ADALONG_COLORS.DARK_GRAY,
      transform: 'translateY(-1px)',
    },
    '&:disabled': {
      cursor: 'wait',
      opacity: 0.9,
    },
  },
});

const generateSuggestionsSpinnerClass = style({
  color: ADALONG_COLORS.LIGHT_YELLOW,
});

const generateSuggestionsErrorClass = style({
  marginTop: 12,
});

const sectionHeaderRowClass = style({
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
});

const suggestedSectionHeaderButtonClass = style({
  cursor: 'pointer',
  borderRadius: 10,
  padding: '2px 0',
  $nest: {
    '&:hover': {
      opacity: 0.95,
    },
  },
});

const sectionHelperTextClass = style({
  display: 'block',
  marginTop: 4,
  color: ADALONG_COLORS.LIGHT_GRAY,
});

const suggestedSectionControlsClass = style({
  flexGrow: 0,
  alignItems: 'center',
  gap: 8,
});

const sectionCountBadgeClass = style({
  minWidth: 28,
  padding: '4px 10px',
  borderRadius: 999,
  textAlign: 'center',
  fontSize: 12,
  fontWeight: 700,
  color: ADALONG_COLORS.DARK_BLACK,
  backgroundColor: ADALONG_COLORS.LIGHT_YELLOW,
});

const suggestedSectionToggleClass = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: '50%',
  cursor: 'pointer',
  color: ADALONG_COLORS.LIGHT_WHITE,
  $nest: {
    '&:hover': {
      backgroundColor: ADALONG_COLORS.DARKER_GRAY,
    },
    '& .material-icons': {
      fontSize: 20,
    },
  },
});

const catalogSectionClass = style({
  marginTop: 8,
});

const productGroupListClass = style({
  marginTop: 20,
});

const catalogSectionHeaderClass = style({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
});

const catalogSectionDividerClass = style({
  flexShrink: 0,
  width: 36,
  height: 1,
  backgroundColor: ADALONG_COLORS.DARK_GRAY,
});
